import { useEffect, useRef, useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Ruler, Calendar, Clock, MapPin, User, Mail, Phone, Bell, AlertTriangle, X, Plus, Trash2, Users, Pencil, CheckCircle, Search, XCircle } from "lucide-react";
import PageLayout from "../components/PageLayout";
import { SkeletonTable } from "@/components/Skeletons";
import { useYear } from "../contexts/YearContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const rilievoEventTitle = (r) => {
  const g = (k) => String(r?.[k] || "").trim();
  const nome = `${g("nome")} ${g("cognome")}`.trim();
  return [`Rilievo ${nome}`.trim(), g("via"), g("civico"), g("piano") && `piano ${g("piano")}`, g("citta"), g("telefono")]
    .filter(Boolean).join(", ");
};

export default function RilievoMisurePage() {
  const { selectedYear } = useYear();
  const { mutate } = useSWRConfig();

  // SWR keys — usate per prefetch/priming e cache localStorage persistente
  const rilieviKey = `/rilievo-misure?year=${selectedYear}`;
  const interviewsKey = `/interviews?year=${selectedYear}&light=true`;

  const { data: rilievi = [], isLoading: loadingRilievi } = useSWR(rilieviKey);
  const { data: interviews = [] } = useSWR(interviewsKey);

  // Skeleton mostrato SOLO se non abbiamo ancora dati (né in-memory né stale).
  const loading = loadingRilievi && rilievi.length === 0;

  const refetchRilievi = () => mutate(rilieviKey);
  const refetchInterviews = () => mutate(interviewsKey);
  const [selectedRilievo, setSelectedRilievo] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isNewRilievoOpen, setIsNewRilievoOpen] = useState(false);
  // Admin actions su Prossimi Rilievi
  const [adminAction, setAdminAction] = useState({ type: null, rilievo: null, data: "", orario: "", nota: "", motivo: "", loading: false });
  const sendReminderManuale = async (r) => {
    if (!window.confirm(`Inviare promemoria email a ${r.cognome} ${r.nome} (${r.email || "no email"})?`)) return;
    try {
      await axios.post(`${API}/rilievo-misure/${r.id}/reminder-manuale`, {}, { withCredentials: true });
      toast.success("Promemoria inviato (copia a showroomingenio@gmail.com)");
      refetchRilievi();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio promemoria");
    }
  };
  const openRiprogramma = (r) => {
    setAdminAction({ type: "riprogramma", rilievo: r, data: r.data_rilievo || "", orario: r.ora_rilievo || "", nota: "", motivo: "", loading: false });
  };
  const openCancella = (r) => {
    setAdminAction({ type: "cancella", rilievo: r, data: "", orario: "", nota: "", motivo: "", loading: false });
  };
  const closeAdminAction = () => setAdminAction({ type: null, rilievo: null, data: "", orario: "", nota: "", motivo: "", loading: false });
  const confirmRiprogramma = async () => {
    if (!adminAction.data || !adminAction.orario) { toast.error("Imposta data e orario"); return; }
    setAdminAction((p) => ({ ...p, loading: true }));
    try {
      await axios.post(`${API}/rilievo-misure/${adminAction.rilievo.id}/riprogramma-admin`,
        { data: adminAction.data, orario: adminAction.orario, nota: adminAction.nota || "" },
        { withCredentials: true });
      toast.success("Rilievo riprogrammato e cliente avvisato");
      closeAdminAction();
      refetchRilievi();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore riprogrammazione");
      setAdminAction((p) => ({ ...p, loading: false }));
    }
  };
  const confirmCancella = async () => {
    setAdminAction((p) => ({ ...p, loading: true }));
    try {
      await axios.post(`${API}/rilievo-misure/${adminAction.rilievo.id}/cancella-admin`,
        { motivo: adminAction.motivo || "" },
        { withCredentials: true });
      toast.success("Rilievo cancellato e cliente avvisato");
      closeAdminAction();
      refetchRilievi();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore cancellazione");
      setAdminAction((p) => ({ ...p, loading: false }));
    }
  };
  const [formData, setFormData] = useState({
    data_rilievo: "",
    ora_rilievo: "",
    indirizzo_rilievo: "",
    note_rilievo: ""
  });
  const [newRilievoData, setNewRilievoData] = useState({
    nome: "",
    cognome: "",
    email: "",
    telefono: "",
    citta: "",
    via: "",
    civico: "",
    piano: "",
    data_rilievo: "",
    ora_rilievo: "",
    indirizzo_rilievo: "",
    note_rilievo: "",
    motivo_rilievo: "",
    interview_id: null,
    invia_email_cliente: true,
  });
  // Flow Nuovo Rilievo stile Appuntamenti
  const [rilievoMode, setRilievoMode] = useState("ricerca"); // 'ricerca' | 'manuale'
  const [contactQuery, setContactQuery] = useState("");
  const [contactResults, setContactResults] = useState([]);
  const [searchingContacts, setSearchingContacts] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsMessage, setSlotsMessage] = useState("");
  const [googleStatus, setGoogleStatus] = useState({ loading: true, connected: false });

  const fetchGoogleStatus = async () => {
    try {
      const r = await axios.get(`${API}/oauth/gmail/status`, { withCredentials: true });
      setGoogleStatus({ loading: false, connected: !!r.data?.connected });
    } catch {
      setGoogleStatus({ loading: false, connected: false });
    }
  };

  const connectGoogle = async () => {
    try {
      const r = await axios.get(`${API}/oauth/gmail/login?return_to=/rilievo-misure`, { withCredentials: true });
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore collegamento Google");
    }
  };

  useEffect(() => { fetchGoogleStatus(); }, []);
  const [showNascosti, setShowNascosti] = useState(false);
  const [nascosti, setNascosti] = useState([]);
  const contactSearchRef = useRef(null);

  const fetchNascosti = async () => {
    try {
      const r = await axios.get(`${API}/rilievo-misure/nascosti?year=${selectedYear}`, { withCredentials: true });
      setNascosti(r.data?.items || []);
    } catch (e) {
      setNascosti([]);
    }
  };

  const handleRiattiva = async (rilievoId) => {
    try {
      await axios.patch(`${API}/rilievo-misure/${rilievoId}/riattiva`, {}, { withCredentials: true });
      toast.success("Rilievo riattivato");
      fetchNascosti();
      fetchRilievi();
    } catch (e) {
      toast.error("Errore riattivazione");
    }
  };
  const [saving, setSaving] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [showWarning, setShowWarning] = useState(true);
  const [notifyingNew, setNotifyingNew] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    nome: "",
    cognome: "",
    email: "",
    telefono: "",
    citta: "",
    via: "",
    civico: "",
    piano: "",
    data_rilievo: "",
    ora_rilievo: "",
    indirizzo_rilievo: "",
    note_rilievo: ""
  });

  const fetchRilievi = async () => {
    // Refresh esplicito post-mutazione: SWR revalida da server e aggiorna cache
    try {
      await refetchRilievi();
      // Riapri il warning se ci sono rilievi da programmare
      const data = rilievi || [];
      const hasUnprogrammed = data.some(r => !r.data_rilievo || r.data_rilievo === "");
      if (hasUnprogrammed) setShowWarning(true);
    } catch (e) {
      console.error("Error fetching rilievi:", e);
      toast.error("Errore nel caricamento dei dati");
    }
  };

  const fetchInterviews = () => refetchInterviews();

  const handleSelectInterview = (interviewId) => {
    if (!interviewId || interviewId === "none") {
      // Reset form
      setNewRilievoData({
        nome: "", cognome: "", email: "", telefono: "",
        citta: "", via: "", civico: "", piano: "",
        data_rilievo: "", ora_rilievo: "", indirizzo_rilievo: "", note_rilievo: ""
      });
      return;
    }
    
    const interview = interviews.find(i => i.id === interviewId);
    if (interview) {
      setNewRilievoData({
        ...newRilievoData,
        nome: interview.nome || "",
        cognome: interview.cognome || "",
        email: interview.email || "",
        telefono: interview.telefono || "",
        citta: interview.citta || "",
        via: interview.via || "",
        civico: interview.civico || "",
        piano: interview.piano || ""
      });
      toast.success(`Dati di ${interview.nome} ${interview.cognome} caricati`);
    }
  };

  const handleOpenDetail = (rilievo) => {
    setSelectedRilievo(rilievo);
    setIsEditing(false);
    setFormData({
      data_rilievo: rilievo.data_rilievo || "",
      ora_rilievo: rilievo.ora_rilievo || "",
      indirizzo_rilievo: rilievo.indirizzo_rilievo || `${rilievo.via || ""} ${rilievo.civico || ""}, ${rilievo.citta || ""}`.trim().replace(/^,\s*/, "").replace(/,\s*$/, ""),
      note_rilievo: rilievo.note_rilievo || ""
    });
    setEditFormData({
      nome: rilievo.nome || "",
      cognome: rilievo.cognome || "",
      email: rilievo.email || "",
      telefono: rilievo.telefono || "",
      citta: rilievo.citta || "",
      via: rilievo.via || "",
      civico: rilievo.civico || "",
      piano: rilievo.piano || "",
      data_rilievo: rilievo.data_rilievo || "",
      ora_rilievo: rilievo.ora_rilievo || "",
      indirizzo_rilievo: rilievo.indirizzo_rilievo || "",
      note_rilievo: rilievo.note_rilievo || ""
    });
    setIsDetailOpen(true);
  };

  // Apre il dialog direttamente in modalità modifica con tutti i dati prefilled
  const handleOpenEdit = (rilievo) => {
    handleOpenDetail(rilievo);
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!selectedRilievo) return;
    setSaving(true);
    try {
      // Se in modalità modifica, salva tutti i campi
      const dataToSave = isEditing ? editFormData : formData;
      await axios.put(`${API}/rilievo-misure/${selectedRilievo.id}`, dataToSave, { withCredentials: true });
      toast.success("Dati salvati con successo");
      fetchRilievi();
      setIsDetailOpen(false);
      setIsEditing(false);
    } catch (e) {
      console.error("Error saving rilievo:", e);
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateNewRilievo = async () => {
    if (!newRilievoData.nome || !newRilievoData.cognome) {
      toast.error("Nome e cognome sono obbligatori");
      return;
    }
    if (newRilievoData.data_rilievo && !newRilievoData.ora_rilievo) {
      toast.error("Seleziona anche l'orario per creare l'evento su Google Calendar");
      return;
    }
    setSaving(true);
    try {
      const response = await axios.post(`${API}/rilievo-misure/nuovo`, newRilievoData, { withCredentials: true });
      if (response.data?.calendar_event_id) {
        toast.success("Rilievo creato e aggiunto al calendario 'Rilievi e Uscite Mario'");
      } else {
        toast.success("Rilievo creato con successo");
      }
      fetchRilievi();
      resetNewRilievoDialog();
    } catch (e) {
      console.error("Error creating rilievo:", e);
      const detail = e?.response?.data?.detail || "Errore nella creazione del rilievo";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const resetNewRilievoDialog = () => {
    setNewRilievoData({
      nome: "", cognome: "", email: "", telefono: "",
      citta: "", via: "", civico: "", piano: "",
      data_rilievo: "", ora_rilievo: "", indirizzo_rilievo: "", note_rilievo: "",
      motivo_rilievo: "", interview_id: null, invia_email_cliente: true,
    });
    setRilievoMode("ricerca");
    setContactQuery("");
    setContactResults([]);
    setSelectedContact(null);
    setSlots([]);
    setSlotsMessage("");
  };

  // Debounce ricerca contatti
  useEffect(() => {
    if (rilievoMode !== "ricerca") return;
    if (contactSearchRef.current) clearTimeout(contactSearchRef.current);
    const q = (contactQuery || "").trim();
    if (q.length < 2) {
      setContactResults([]);
      return;
    }
    contactSearchRef.current = setTimeout(async () => {
      setSearchingContacts(true);
      try {
        const res = await axios.get(`${API}/contacts/search`, {
          params: { q, limit: 25 },
          withCredentials: true,
        });
        // Prediligi interviste
        const items = (res.data?.results || []).sort((a, b) =>
          (a.source === "intervista" ? -1 : 1) - (b.source === "intervista" ? -1 : 1)
        );
        setContactResults(items);
      } catch (e) {
        setContactResults([]);
      } finally {
        setSearchingContacts(false);
      }
    }, 300);
    return () => contactSearchRef.current && clearTimeout(contactSearchRef.current);
  }, [contactQuery, rilievoMode]);

  const handlePickContact = (c) => {
    setSelectedContact(c);
    setNewRilievoData((prev) => ({
      ...prev,
      nome: c.nome || "",
      cognome: c.cognome || "",
      email: c.email || "",
      telefono: c.telefono || "",
      interview_id: c.source === "intervista" ? c.id : null,
    }));
    setContactResults([]);
    setContactQuery(`${c.cognome || ""} ${c.nome || ""}`.trim());
  };

  const clearSelectedContact = () => {
    setSelectedContact(null);
    setNewRilievoData((prev) => ({ ...prev, nome: "", cognome: "", email: "", telefono: "", interview_id: null }));
    setContactQuery("");
  };

  // Fetch slot liberi quando cambia la data
  useEffect(() => {
    const d = newRilievoData.data_rilievo;
    if (!d) {
      setSlots([]);
      setSlotsMessage("");
      return;
    }
    (async () => {
      setSlotsLoading(true);
      setSlotsMessage("");
      try {
        const res = await axios.get(`${API}/rilievo-misure/slots`, {
          params: { data_str: d },
          withCredentials: true,
        });
        if (res.data?.closed) {
          setSlots([]);
          setSlotsMessage(res.data?.reason || "Giorno chiuso");
          return;
        }
        setSlots(res.data?.slots || []);
        if (res.data?.google_connected === false) {
          setSlotsMessage("⚠️ Google Calendar non collegato: non posso verificare i conflitti. Collega Google da Scadenzario Pagamenti per vedere solo gli slot realmente liberi.");
        }
      } catch (e) {
        setSlots([]);
        setSlotsMessage("Errore caricamento slot");
      } finally {
        setSlotsLoading(false);
      }
    })();
  }, [newRilievoData.data_rilievo]);

  const handleNotificaCliente = async () => {
    if (!selectedRilievo) return;
    setNotifying(true);
    try {
      await axios.post(`${API}/rilievo-misure/${selectedRilievo.id}/notifica-cliente`, {}, { withCredentials: true });
      toast.success("Notifica inviata al cliente");
    } catch (e) {
      console.error("Error notifying client:", e);
      toast.error(e.response?.data?.detail || "Errore nell'invio della notifica");
    } finally {
      setNotifying(false);
    }
  };

  const handleDeleteRilievo = async (rilievoId, e) => {
    e.stopPropagation();
    if (!confirm("Sei sicuro di voler eliminare questo rilievo?")) return;
    
    try {
      await axios.delete(`${API}/rilievo-misure/${rilievoId}`, { withCredentials: true });
      toast.success("Rilievo eliminato");
      fetchRilievi();
    } catch (e) {
      console.error("Error deleting rilievo:", e);
      toast.error("Errore nell'eliminazione del rilievo");
    }
  };

  const [bulkDeleting, setBulkDeleting] = useState(false);
  const completatiCount = (rilievi || []).filter(r => r.stato_rilievo === "completato").length;
  const handleBulkDeleteCompletati = async () => {
    if (completatiCount === 0) { toast.info("Nessun rilievo completato da eliminare"); return; }
    if (!confirm(`Eliminare TUTTI i ${completatiCount} rilievi completati?\n\nLe offerte verranno nascoste dalla pagina rilievi (restano nel CRM), i rilievi manuali verranno cancellati definitivamente.`)) return;
    setBulkDeleting(true);
    try {
      const res = await axios.delete(`${API}/rilievo-misure/bulk-completati`, { withCredentials: true });
      toast.success(`Eliminati ${res.data?.total || 0} rilievi completati`);
      fetchRilievi();
    } catch (e) {
      console.error("Error bulk delete completati:", e);
      toast.error(e.response?.data?.detail || "Errore eliminazione in bulk");
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleRilievoCompletato = async () => {
    if (!selectedRilievo) return;
    if (!selectedRilievo.email) {
      toast.error("Email cliente non disponibile");
      return;
    }
    
    if (!confirm("Confermi di voler segnare il rilievo come completato e inviare la notifica al cliente?")) {
      return;
    }
    
    setCompleting(true);
    try {
      await axios.post(`${API}/rilievo-misure/${selectedRilievo.id}/completa`, {}, { withCredentials: true });
      toast.success("Rilievo completato e notifica inviata al cliente");
      fetchRilievi();
      setIsDetailOpen(false);
    } catch (e) {
      console.error("Error completing rilievo:", e);
      toast.error(e.response?.data?.detail || "Errore nel completamento del rilievo");
    } finally {
      setCompleting(false);
    }
  };

  const handleNotificaNuovoRilievo = async () => {
    if (!newRilievoData.email) {
      toast.error("Inserisci l'email del cliente per inviare la notifica");
      return;
    }
    if (!newRilievoData.data_rilievo) {
      toast.error("Inserisci la data del rilievo prima di notificare");
      return;
    }
    
    setNotifyingNew(true);
    try {
      // Costruisce l'indirizzo
      const indirizzo = newRilievoData.indirizzo_rilievo || 
        `${newRilievoData.via || ""} ${newRilievoData.civico || ""}, ${newRilievoData.citta || ""}`.trim();
      
      await axios.post(`${API}/rilievo-misure/notifica-preview`, {
        nome: newRilievoData.nome,
        cognome: newRilievoData.cognome,
        email: newRilievoData.email,
        data_rilievo: newRilievoData.data_rilievo,
        ora_rilievo: newRilievoData.ora_rilievo,
        indirizzo_rilievo: indirizzo
      }, { withCredentials: true });
      toast.success("Notifica inviata al cliente");
    } catch (e) {
      console.error("Error notifying client:", e);
      toast.error(e.response?.data?.detail || "Errore nell'invio della notifica");
    } finally {
      setNotifyingNew(false);
    }
  };

  const handleCalendarioNuovoRilievo = () => {
    if (!newRilievoData.data_rilievo) {
      toast.error("Seleziona prima una data per il rilievo");
      return;
    }
    
    const indirizzo = newRilievoData.indirizzo_rilievo || 
      `${newRilievoData.via || ""} ${newRilievoData.civico || ""}, ${newRilievoData.citta || ""}`.trim();
    const note = newRilievoData.note_rilievo || "";
    
    const titolo = rilievoEventTitle(newRilievoData);
    
    // Formatta data e ora per Google Calendar
    const dataBase = newRilievoData.data_rilievo.replace(/-/g, "");
    let dataInizio = "";
    let dataFine = "";
    
    if (newRilievoData.ora_rilievo) {
      const oraBase = newRilievoData.ora_rilievo.replace(":", "") + "00";
      dataInizio = `${dataBase}T${oraBase}`;
      const [ore, minuti] = newRilievoData.ora_rilievo.split(":").map(Number);
      const oreFine = String(ore + 2).padStart(2, "0");
      const oraFine = `${oreFine}${String(minuti).padStart(2, "0")}00`;
      dataFine = `${dataBase}T${oraFine}`;
    } else {
      dataInizio = dataBase;
      dataFine = dataBase;
    }
    
    const calendarId = "ks2n5mj950h7i0bpfelh550no8@group.calendar.google.com";
    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: titolo,
      dates: `${dataInizio}/${dataFine}`,
      details: note || "Rilievo misure",
      location: indirizzo,
      src: calendarId
    });
    
    window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, "_blank");
    toast.success("Calendario aperto in una nuova finestra");
  };

  const handleAggiungiAlCalendario = () => {
    if (!selectedRilievo) return;
    
    const indirizzo = formData.indirizzo_rilievo || `${selectedRilievo.via || ""} ${selectedRilievo.civico || ""}, ${selectedRilievo.citta || ""}`.trim();
    const note = formData.note_rilievo || "";
    
    const titolo = rilievoEventTitle(selectedRilievo);
    
    // Formatta data e ora per Google Calendar (formato: YYYYMMDDTHHMMSS)
    let dataInizio = "";
    let dataFine = "";
    
    if (formData.data_rilievo) {
      // Rimuovi i trattini dalla data (YYYY-MM-DD -> YYYYMMDD)
      const dataBase = formData.data_rilievo.replace(/-/g, "");
      
      if (formData.ora_rilievo) {
        // Con orario specifico
        const oraBase = formData.ora_rilievo.replace(":", "") + "00"; // HH:MM -> HHMMSS
        dataInizio = `${dataBase}T${oraBase}`;
        
        // Durata di 2 ore per default
        const [ore, minuti] = formData.ora_rilievo.split(":").map(Number);
        const oreFine = String(ore + 2).padStart(2, "0");
        const oraFine = `${oreFine}${String(minuti).padStart(2, "0")}00`;
        dataFine = `${dataBase}T${oraFine}`;
      } else {
        // Evento giornata intera
        dataInizio = dataBase;
        dataFine = dataBase;
      }
    } else {
      toast.error("Seleziona prima una data per il rilievo");
      return;
    }
    
    // ID del calendario "Rilievi e Uscite Mario"
    const calendarId = "ks2n5mj950h7i0bpfelh550no8@group.calendar.google.com";
    
    // Costruisci l'URL di Google Calendar con il calendario specifico
    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: titolo,
      dates: `${dataInizio}/${dataFine}`,
      details: note || "Rilievo misure",
      location: indirizzo,
      src: calendarId
    });
    
    const calendarUrl = `https://calendar.google.com/calendar/render?${params.toString()}`;
    
    // Apri in una nuova finestra
    window.open(calendarUrl, "_blank");
    toast.success("Calendario aperto in una nuova finestra");
  };

  const rilievoSenzaData = (r) => !r.data_rilievo || r.data_rilievo === "";

  // Formatta la data da YYYY-MM-DD a gg-mm-aaaa
  const formatDataItaliana = (data) => {
    if (!data) return null;
    try {
      const parts = data.split("-");
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    } catch {
      return data;
    }
  };

  if (loading) {
    return (
      <PageLayout title="Rilievo Misure" subtitle="Gestione appuntamenti per rilievi">
        <div className="p-4 lg:p-6 space-y-6">
          <div className="mb-4">
            <div className="h-8 bg-slate-200 rounded w-64 animate-pulse mb-2" />
            <div className="h-4 bg-slate-100 rounded w-96 animate-pulse" />
          </div>
          <SkeletonTable rows={8} cols={5} />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Rilievo Misure" subtitle="Gestione appuntamenti per rilievi">
      <div className="p-4 lg:p-6 flex flex-col gap-6">
        {/* Header con titolo */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl lg:text-2xl xl:text-3xl font-bold text-slate-900">Rilievo Misure</h1>
            <p className="text-sm text-slate-500 mt-1">Gestione appuntamenti per rilievi tecnici</p>
          </div>
        </div>

        {/* Banner stato Google Calendar */}
        {!googleStatus.loading && !googleStatus.connected && (
          <div
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-red-50 border-2 border-red-300 rounded-xl p-4"
            data-testid="google-not-connected-banner"
          >
            <div className="flex items-start gap-3">
              <XCircle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-900">Google Calendar non collegato</p>
                <p className="text-sm text-red-800 mt-0.5">
                  Senza connessione non posso verificare i conflitti sul calendario <strong>"Rilievi e Uscite Mario"</strong> e tutti gli slot vengono mostrati come liberi.
                </p>
              </div>
            </div>
            <Button
              type="button"
              onClick={connectGoogle}
              data-state="colored"
              style={{ backgroundColor: "#dc2626" }}
              className="text-white hover:opacity-90 shrink-0"
              data-testid="btn-collega-google"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Collega Google
            </Button>
          </div>
        )}
        {!googleStatus.loading && googleStatus.connected && (
          <div
            className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm text-emerald-800"
            data-testid="google-connected-banner"
          >
            <CheckCircle className="w-4 h-4 text-emerald-600" />
            Google Calendar collegato: slot sincronizzati con <strong>"Rilievi e Uscite Mario"</strong>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Totale Rilievi</p>
                  <p className="text-3xl font-bold text-slate-900">{rilievi.length}</p>
                </div>
                <Ruler className="w-10 h-10 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Da Programmare</p>
                  <p className="text-3xl font-bold text-amber-600">{rilievi.filter(r => rilievoSenzaData(r) && r.stato_rilievo !== "completato").length}</p>
                </div>
                <Calendar className="w-10 h-10 text-amber-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Programmati</p>
                  <p className="text-3xl font-bold text-blue-600">{rilievi.filter(r => !rilievoSenzaData(r) && r.stato_rilievo !== "completato").length}</p>
                </div>
                <Clock className="w-10 h-10 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-600">Completati</p>
                  <p className="text-3xl font-bold text-emerald-700">{rilievi.filter(r => r.stato_rilievo === "completato").length}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-emerald-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Vista Nascosti Toggle */}
        <div className="flex items-center justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-state="colored"
            style={{ backgroundColor: showNascosti ? "#dc2626" : "#ffffff" }}
            className={showNascosti ? "text-white hover:opacity-90" : "text-slate-700"}
            onClick={() => { const next = !showNascosti; setShowNascosti(next); if (next) fetchNascosti(); }}
            data-testid="btn-vista-nascosti"
          >
            {showNascosti ? "Nascondi archiviati" : "Mostra archiviati"}
          </Button>
        </div>

        {showNascosti && (
          <Card className="border-red-200 bg-red-50/40">
            <CardHeader>
              <CardTitle className="text-sm">Rilievi archiviati · {nascosti.length}</CardTitle>
            </CardHeader>
            <CardContent>
              {nascosti.length === 0 ? (
                <p className="text-sm italic text-slate-500">Nessun rilievo archiviato.</p>
              ) : (
                <div className="divide-y divide-slate-200">
                  {nascosti.map((n) => (
                    <div key={`${n.source}-${n.id}`} className="flex items-center justify-between py-2" data-testid={`nascosto-${n.id}`}>
                      <div>
                        <p className="font-medium text-sm text-slate-900">{n.cognome} {n.nome}</p>
                        <p className="text-xs text-slate-500">{n.telefono || n.email || "-"} · {n.data_rilievo ? `${n.data_rilievo} ${n.ora_rilievo || ""}` : "senza data"} · {n.source}</p>
                      </div>
                      <Button type="button" size="sm" variant="outline" onClick={() => handleRiattiva(n.id)} data-testid={`btn-riattiva-${n.id}`}>
                        Riattiva
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Table */}
        <Card className="order-5">
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="flex items-center gap-2">
                <Ruler className="w-5 h-5" />
                Elenco Rilievi
              </CardTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleBulkDeleteCompletati}
                disabled={bulkDeleting || completatiCount === 0}
                className="border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
                data-testid="bulk-delete-completati-btn"
                title={completatiCount === 0 ? "Nessun rilievo completato" : `Elimina tutti i ${completatiCount} rilievi completati`}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {bulkDeleting ? "Eliminazione..." : `Elimina Completati${completatiCount > 0 ? ` (${completatiCount})` : ""}`}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="rilievi-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Cliente</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Data Rilievo</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Ora</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Indirizzo</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Stato</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {rilievi.map((rilievo) => (
                    <tr 
                      key={rilievo.id} 
                      className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`rilievo-row-${rilievo.id}`}
                    >
                      <td className="py-3 px-4">
                        <span className="text-slate-900 font-medium">
                          {rilievo.nome} {rilievo.cognome}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {rilievo.data_rilievo ? formatDataItaliana(rilievo.data_rilievo) : (
                          <span className="text-amber-600 text-sm">Da programmare</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {rilievo.ora_rilievo || "-"}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {rilievo.indirizzo_rilievo || `${rilievo.via || ""} ${rilievo.civico || ""}, ${rilievo.citta || ""}`.trim() || "-"}
                      </td>
                      <td className="py-3 px-4">
                        {rilievo.stato_rilievo === "completato" ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700">
                            Completato
                          </span>
                        ) : rilievoSenzaData(rilievo) ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                            Da programmare
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                            Programmato
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleOpenEdit(rilievo)}
                            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Modifica rilievo"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => handleDeleteRilievo(rilievo.id, e)}
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Elimina rilievo"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {rilievi.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-slate-500">
                        Nessun rilievo trovato
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={isDetailOpen} onOpenChange={(open) => { setIsDetailOpen(open); if (!open) setIsEditing(false); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-slate-600" />
                {isEditing ? "Modifica Rilievo" : "Dettagli Rilievo"}
              </DialogTitle>
            </DialogHeader>
            {selectedRilievo && (
              <div className="space-y-6">
                {/* Pulsante Modifica in alto (solo in visualizzazione) */}
                {!isEditing && (
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditing(true)}
                      className="text-blue-600 border-blue-300 hover:bg-blue-50"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Modifica Dati
                    </Button>
                  </div>
                )}
                
                {isEditing ? (
                  /* Modalità Modifica - Tutti i campi editabili */
                  <>
                    {/* Dati Cliente */}
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-900 mb-3">Dati Cliente</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="edit-nome">Nome</Label>
                          <Input
                            id="edit-nome"
                            value={editFormData.nome}
                            onChange={(e) => setEditFormData({ ...editFormData, nome: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-cognome">Cognome</Label>
                          <Input
                            id="edit-cognome"
                            value={editFormData.cognome}
                            onChange={(e) => setEditFormData({ ...editFormData, cognome: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-email">Email</Label>
                          <Input
                            id="edit-email"
                            type="email"
                            value={editFormData.email}
                            onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-telefono">Telefono</Label>
                          <Input
                            id="edit-telefono"
                            value={editFormData.telefono}
                            onChange={(e) => setEditFormData({ ...editFormData, telefono: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Indirizzo */}
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-900 mb-3">Indirizzo</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="edit-citta">Città</Label>
                          <Input
                            id="edit-citta"
                            value={editFormData.citta}
                            onChange={(e) => setEditFormData({ ...editFormData, citta: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-via">Via</Label>
                          <Input
                            id="edit-via"
                            value={editFormData.via}
                            onChange={(e) => setEditFormData({ ...editFormData, via: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-civico">Civico</Label>
                          <Input
                            id="edit-civico"
                            value={editFormData.civico}
                            onChange={(e) => setEditFormData({ ...editFormData, civico: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-piano">Piano</Label>
                          <Input
                            id="edit-piano"
                            value={editFormData.piano}
                            onChange={(e) => setEditFormData({ ...editFormData, piano: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Dati Rilievo */}
                    <div className="bg-emerald-50 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-900 mb-3">Dati Rilievo</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="edit-data_rilievo">Data Rilievo</Label>
                          <Input
                            id="edit-data_rilievo"
                            type="date"
                            value={editFormData.data_rilievo}
                            onChange={(e) => setEditFormData({ ...editFormData, data_rilievo: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-ora_rilievo">Ora Rilievo</Label>
                          <Input
                            id="edit-ora_rilievo"
                            type="time"
                            value={editFormData.ora_rilievo}
                            onChange={(e) => setEditFormData({ ...editFormData, ora_rilievo: e.target.value })}
                          />
                        </div>
                        <div className="col-span-2">
                          <Label htmlFor="edit-indirizzo_rilievo">Indirizzo Rilievo (se diverso)</Label>
                          <Input
                            id="edit-indirizzo_rilievo"
                            value={editFormData.indirizzo_rilievo}
                            onChange={(e) => setEditFormData({ ...editFormData, indirizzo_rilievo: e.target.value })}
                          />
                        </div>
                        <div className="col-span-2">
                          <Label htmlFor="edit-note_rilievo">Note</Label>
                          <Textarea
                            id="edit-note_rilievo"
                            value={editFormData.note_rilievo}
                            onChange={(e) => setEditFormData({ ...editFormData, note_rilievo: e.target.value })}
                            rows={3}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons - Edit Mode */}
                    <div className="flex gap-3 pt-4 border-t">
                      <Button
                        variant="outline"
                        onClick={() => setIsEditing(false)}
                        className="flex-1"
                      >
                        Annulla
                      </Button>
                      <Button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex-1 bg-[#1e40af] hover:bg-[#1e3a8a] text-white"
                      >
                        {saving ? "Salvataggio..." : "Salva Modifiche"}
                      </Button>
                    </div>
                  </>
                ) : (
                  /* Modalità Visualizzazione */
                  <>
                    {/* Client Info */}
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-900 mb-3">Dati Cliente</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">{selectedRilievo.nome} {selectedRilievo.cognome}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-slate-400" />
                          <span>{selectedRilievo.email || "N/D"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-slate-400" />
                          <span>{selectedRilievo.telefono || "N/D"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <span>{selectedRilievo.via} {selectedRilievo.civico}, {selectedRilievo.citta}</span>
                        </div>
                      </div>
                    </div>

                    {/* Form Fields */}
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="data_rilievo">Data Rilievo</Label>
                          <Input
                            id="data_rilievo"
                            type="date"
                            value={formData.data_rilievo}
                            onChange={(e) => setFormData({ ...formData, data_rilievo: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label htmlFor="ora_rilievo">Ora Rilievo</Label>
                          <Input
                            id="ora_rilievo"
                            type="time"
                            value={formData.ora_rilievo}
                            onChange={(e) => setFormData({ ...formData, ora_rilievo: e.target.value })}
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="indirizzo_rilievo">Indirizzo</Label>
                        <Input
                          id="indirizzo_rilievo"
                          value={formData.indirizzo_rilievo}
                          onChange={(e) => setFormData({ ...formData, indirizzo_rilievo: e.target.value })}
                          placeholder="Indirizzo per il rilievo"
                        />
                      </div>

                      <div>
                        <Label htmlFor="note_rilievo">Note</Label>
                        <Textarea
                          id="note_rilievo"
                          value={formData.note_rilievo}
                          onChange={(e) => setFormData({ ...formData, note_rilievo: e.target.value })}
                          placeholder="Note per il tecnico..."
                          rows={3}
                        />
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-3 pt-4 border-t">
                      <div className="flex gap-2">
                        <Button
                          onClick={handleNotificaCliente}
                          disabled={notifying}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <Bell className="w-4 h-4 mr-2" />
                          Notifica Cliente
                        </Button>
                        <Button
                          onClick={handleAggiungiAlCalendario}
                          className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                        >
                          <Calendar className="w-4 h-4 mr-2" />
                          Aggiungi al Calendario
                        </Button>
                      </div>
                      
                      {/* Pulsante Rilievo Completato */}
                      {selectedRilievo.stato_rilievo !== "completato" && (
                        <Button
                          onClick={handleRilievoCompletato}
                          disabled={completing}
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          {completing ? "Completamento in corso..." : "Rilievo Completato"}
                        </Button>
                      )}
                      {selectedRilievo.stato_rilievo === "completato" && (
                        <div className="text-center py-2 px-4 bg-emerald-100 text-emerald-700 rounded-lg font-medium">
                          <CheckCircle className="w-4 h-4 inline mr-2" />
                          Rilievo già completato
                        </div>
                      )}
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setIsDetailOpen(false)}
                          className="flex-1"
                        >
                          Chiudi
                        </Button>
                        <Button
                          onClick={handleSave}
                          disabled={saving}
                          className="flex-1 bg-[#1e40af] hover:bg-[#1e3a8a] text-white"
                        >
                          {saving ? "Salvataggio..." : "Salva Modifiche"}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Dialog Nuovo Rilievo */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 order-4" data-testid="nuovo-rilievo-grid">
          <Card className="lg:col-span-2" data-testid="nuovo-rilievo-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Ruler className="w-5 h-5 text-blue-600" />
                Nuovo Rilievo
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                Verrà creato sul Google Calendar <strong>Rilievi e Uscite Mario</strong>.
                Slot da 30 minuti · Lun-Ven 9:30-13:30 e 16:30-19:30 · Sabato 9:30-13:30
              </p>
            </CardHeader>
            <CardContent>
            <div className="space-y-5">
              {/* Toggle Modalità */}
              <div className="inline-flex rounded-lg border border-slate-200 bg-slate-100 p-1" data-testid="rilievo-mode-toggle">
                <button
                  type="button"
                  data-state="colored"
                  style={{ backgroundColor: rilievoMode === "ricerca" ? "#2563eb" : "transparent" }}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    rilievoMode === "ricerca" ? "text-white shadow" : "bg-transparent text-slate-700 hover:bg-slate-200"
                  }`}
                  onClick={() => { setRilievoMode("ricerca"); }}
                  data-testid="mode-ricerca"
                >
                  <Search className="w-4 h-4 inline mr-1" />
                  Da Intervista
                </button>
                <button
                  type="button"
                  data-state="colored"
                  style={{ backgroundColor: rilievoMode === "manuale" ? "#2563eb" : "transparent" }}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    rilievoMode === "manuale" ? "text-white shadow" : "bg-transparent text-slate-700 hover:bg-slate-200"
                  }`}
                  onClick={() => { setRilievoMode("manuale"); clearSelectedContact(); }}
                  data-testid="mode-manuale"
                >
                  <Plus className="w-4 h-4 inline mr-1" />
                  Nuovo da zero
                </button>
              </div>

              {/* Ricerca contatti */}
              {rilievoMode === "ricerca" && (
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Cerca cliente da Interviste / Contratti</Label>
                  {selectedContact ? (
                    <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900">
                          {selectedContact.cognome} {selectedContact.nome}
                        </p>
                        <p className="text-xs text-slate-600 truncate">
                          {selectedContact.email || <span className="italic text-slate-500">(senza email)</span>}
                          {selectedContact.telefono && <> · {selectedContact.telefono}</>}
                          <span className="ml-2 text-slate-400">({selectedContact.source})</span>
                        </p>
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={clearSelectedContact} data-testid="clear-contact">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                        <Input
                          value={contactQuery}
                          onChange={(e) => setContactQuery(e.target.value)}
                          placeholder="Cerca per cognome, nome o telefono (min. 2 caratteri)"
                          className="pl-9"
                          data-testid="contact-search-input"
                          autoFocus
                        />
                      </div>
                      {contactQuery.trim().length >= 2 && (
                        <div className="mt-1 border border-slate-200 rounded-lg bg-white shadow-sm max-h-56 overflow-y-auto">
                          {searchingContacts && <p className="text-xs text-slate-500 italic p-3">Ricerca...</p>}
                          {!searchingContacts && contactResults.length === 0 && (
                            <p className="text-xs text-slate-500 italic p-3">Nessun contatto trovato</p>
                          )}
                          {!searchingContacts && contactResults.map((c) => (
                            <button
                              key={`${c.source}-${c.id}`}
                              type="button"
                              onClick={() => handlePickContact(c)}
                              className="block w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-100 last:border-0 bg-transparent"
                              data-testid={`contact-result-${c.id}`}
                            >
                              <div className="font-medium text-sm text-slate-900">{c.cognome} {c.nome}</div>
                              <div className="text-xs text-slate-600 truncate">
                                {c.telefono || c.email || <span className="italic">senza contatto</span>}
                                <span className="ml-2 text-slate-400">({c.source})</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Dati Cliente (sempre visibili - prefill o manual) */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-semibold text-slate-900 mb-3 text-sm">Dati Cliente</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="new-cognome" className="text-xs">Cognome *</Label>
                    <Input
                      id="new-cognome"
                      value={newRilievoData.cognome}
                      onChange={(e) => setNewRilievoData({ ...newRilievoData, cognome: e.target.value })}
                      placeholder="Cognome"
                      data-testid="input-cognome"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-nome" className="text-xs">Nome *</Label>
                    <Input
                      id="new-nome"
                      value={newRilievoData.nome}
                      onChange={(e) => setNewRilievoData({ ...newRilievoData, nome: e.target.value })}
                      placeholder="Nome"
                      data-testid="input-nome"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-telefono" className="text-xs">Telefono</Label>
                    <Input
                      id="new-telefono"
                      value={newRilievoData.telefono}
                      onChange={(e) => setNewRilievoData({ ...newRilievoData, telefono: e.target.value })}
                      placeholder="Telefono"
                      data-testid="input-telefono"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-email" className="text-xs">Email</Label>
                    <Input
                      id="new-email"
                      type="email"
                      value={newRilievoData.email}
                      onChange={(e) => setNewRilievoData({ ...newRilievoData, email: e.target.value })}
                      placeholder="email@esempio.com"
                      data-testid="input-email"
                    />
                  </div>
                </div>
              </div>

              {/* Motivo */}
              <div>
                <Label htmlFor="new-motivo" className="text-sm font-semibold">
                  Motivo Rilievo <span className="text-slate-400 font-normal">— apparirà nella descrizione dell'evento</span>
                </Label>
                <Input
                  id="new-motivo"
                  value={newRilievoData.motivo_rilievo}
                  onChange={(e) => setNewRilievoData({ ...newRilievoData, motivo_rilievo: e.target.value })}
                  placeholder="Es. Rilievo bagno, misura cucina..."
                  data-testid="input-motivo-rilievo"
                />
              </div>

              {/* Data + Orario (stile Appuntamenti) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="new-data" className="text-sm font-semibold">Data Rilievo *</Label>
                  <Input
                    id="new-data"
                    type="date"
                    value={newRilievoData.data_rilievo}
                    onChange={(e) => setNewRilievoData({ ...newRilievoData, data_rilievo: e.target.value, ora_rilievo: "" })}
                    min={new Date().toISOString().split("T")[0]}
                    data-testid="input-data"
                  />
                </div>
                <div>
                  <Label htmlFor="new-orario" className="text-sm font-semibold">Orario *</Label>
                  {!newRilievoData.data_rilievo ? (
                    <p className="text-sm text-slate-500 py-2 italic">Scegli prima una data</p>
                  ) : slotsLoading ? (
                    <div className="h-10 flex items-center gap-2 text-slate-500 text-sm">
                      <Clock className="w-4 h-4 animate-pulse" /> Caricamento slot...
                    </div>
                  ) : (() => {
                    const liberi = (slots || []).filter((s) => s.libero);
                    if (slotsMessage && slots.length === 0) {
                      return <p className="text-sm text-red-600 py-2">⛔ {slotsMessage}</p>;
                    }
                    if (liberi.length === 0) {
                      return <p className="text-sm text-amber-700 py-2">Nessuno slot libero in questa giornata. Prova un'altra data.</p>;
                    }
                    return (
                      <div className="grid grid-cols-4 gap-1.5 mt-1 max-h-[168px] overflow-y-auto p-1 border rounded" data-testid="slots-grid">
                        {liberi.map((s) => {
                          const isActive = newRilievoData.ora_rilievo === s.orario;
                          return (
                            <button
                              key={s.orario}
                              type="button"
                              onClick={() => setNewRilievoData({ ...newRilievoData, ora_rilievo: s.orario })}
                              data-testid={`slot-${s.orario}`}
                              data-state="colored"
                              style={{
                                backgroundColor: isActive ? "#2563eb" : "#ffffff",
                                color: isActive ? "#ffffff" : "#1e293b",
                                borderColor: isActive ? "#1d4ed8" : "#cbd5e1",
                              }}
                              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = "#eff6ff"; }}
                              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = "#ffffff"; }}
                              className="text-xs px-2 py-1.5 rounded font-medium border transition-colors"
                            >
                              {s.orario}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </div>
              {slotsMessage && slots.length > 0 && (
                <p className="text-xs text-amber-700 italic -mt-2">{slotsMessage}</p>
              )}

              {/* Note */}
              <div>
                <Label htmlFor="new-note" className="text-sm font-semibold">
                  Note <span className="text-slate-400 font-normal">— verranno copiate nella descrizione dell'evento</span>
                </Label>
                <Textarea
                  id="new-note"
                  value={newRilievoData.note_rilievo}
                  onChange={(e) => setNewRilievoData({ ...newRilievoData, note_rilievo: e.target.value })}
                  placeholder="Dettagli per il tecnico, indirizzo preciso, campanello..."
                  rows={3}
                  data-testid="input-note"
                />
              </div>

              {/* Toggle invio email cliente */}
              <label
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  newRilievoData.invia_email_cliente
                    ? "bg-blue-50 border-blue-300"
                    : "bg-slate-50 border-slate-300"
                }`}
                data-testid="toggle-invia-email-label"
              >
                <input
                  type="checkbox"
                  checked={newRilievoData.invia_email_cliente}
                  onChange={(e) => setNewRilievoData({ ...newRilievoData, invia_email_cliente: e.target.checked })}
                  className="w-4 h-4 mt-0.5 accent-blue-600"
                  data-testid="toggle-invia-email"
                />
                <div className="flex-1 text-sm">
                  <p className={`font-semibold ${newRilievoData.invia_email_cliente ? "text-blue-900" : "text-slate-700"}`}>
                    {newRilievoData.invia_email_cliente ? "✉️ Invia email di conferma al cliente" : "🚫 Non inviare email al cliente"}
                  </p>
                  <p className={`text-xs mt-0.5 ${newRilievoData.invia_email_cliente ? "text-blue-700" : "text-slate-500"}`}>
                    {newRilievoData.invia_email_cliente
                      ? "Il cliente riceverà subito una email con data/ora e link al portale per riprogrammare o cancellare."
                      : "Nessuna comunicazione automatica al cliente. Il reminder 24h prima resta comunque attivo."}
                  </p>
                </div>
              </label>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t">
                <Button
                  onClick={handleCreateNewRilievo}
                  disabled={saving || !newRilievoData.cognome || !newRilievoData.nome}
                  className="flex-1 bg-[#1e40af] hover:bg-[#1e3a8a] text-white"
                  data-testid="btn-crea-rilievo"
                >
                  <Ruler className="w-4 h-4 mr-2" />
                  {saving ? "Creazione..." : "Crea Rilievo"}
                </Button>
                <Button variant="outline" onClick={() => { resetNewRilievoDialog(); }} className="shrink-0" data-testid="btn-reset">
                  Reset form
                </Button>
              </div>
            </div>
            </CardContent>
          </Card>

          {/* Lista Prossimi Rilievi */}
          <Card data-testid="prossimi-rilievi-card">
            <CardHeader>
              <CardTitle className="text-lg">Prossimi Rilievi</CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const todayIso = new Date().toISOString().split("T")[0];
                const prossimi = (rilievi || [])
                  .filter(r => r.data_rilievo && r.data_rilievo >= todayIso && r.workflow_stato !== "chiuso" && !r.cancellato_at)
                  .sort((a, b) => {
                    const ka = `${a.data_rilievo} ${a.ora_rilievo || "00:00"}`;
                    const kb = `${b.data_rilievo} ${b.ora_rilievo || "00:00"}`;
                    return ka.localeCompare(kb);
                  })
                  .slice(0, 15);
                if (prossimi.length === 0) {
                  return <p className="text-sm text-slate-500 italic">Nessun rilievo in programma.</p>;
                }
                const fmtIt = (iso) => iso ? iso.split("-").reverse().join("-") : "";
                return (
                  <ul className="space-y-3 max-h-[600px] overflow-y-auto">
                    {prossimi.map((r) => (
                      <li
                        key={r.id}
                        className="p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                        data-testid={`prossimo-rilievo-${r.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-slate-900 text-sm">
                              {fmtIt(r.data_rilievo)} · {r.ora_rilievo || "—"}
                            </p>
                            <p className="text-sm text-slate-800 mt-0.5 flex items-center gap-1.5">
                              <User className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                              {r.cognome} {r.nome}
                            </p>
                            {r.telefono && (
                              <p className="text-xs text-slate-600 flex items-center gap-1.5">
                                <Phone className="w-3 h-3 text-slate-400 shrink-0" />
                                {r.telefono}
                              </p>
                            )}
                            {r.motivo_rilievo && (
                              <p className="text-xs text-slate-700 mt-1 line-clamp-2">
                                {r.motivo_rilievo}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col gap-1 shrink-0">
                            {r.calendar_event_link && (
                              <a
                                href={r.calendar_event_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded bg-transparent"
                                title="Apri su Google Calendar"
                                data-testid={`open-gcal-${r.id}`}
                              >
                                <Calendar className="w-4 h-4" />
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={() => handleOpenEdit(r)}
                              className="p-1.5 text-slate-700 hover:bg-slate-100 rounded bg-transparent border border-slate-300"
                              title="Modifica"
                              data-testid={`edit-rilievo-${r.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => sendReminderManuale(r)}
                              className="p-1.5 text-amber-700 hover:bg-amber-50 rounded bg-transparent border border-amber-300"
                              title="Ricorda al cliente"
                              data-testid={`reminder-rilievo-${r.id}`}
                            >
                              <Bell className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => openRiprogramma(r)}
                              className="p-1.5 text-blue-700 hover:bg-blue-50 rounded bg-transparent border border-blue-300"
                              title="Riprogramma (avvisa cliente)"
                              data-testid={`riprogramma-rilievo-${r.id}`}
                            >
                              <Clock className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => openCancella(r)}
                              className="p-1.5 text-red-700 hover:bg-red-50 rounded bg-transparent border border-red-300"
                              title="Cancella (avvisa cliente)"
                              data-testid={`cancella-rilievo-${r.id}`}
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </CardContent>
          </Card>
        </div>

        {/* Warning Notification */}
        {showWarning && rilievi.filter(rilievoSenzaData).length > 0 && (
          <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-right-5">
            <div className="bg-amber-50 border border-amber-300 rounded-lg shadow-lg p-4 max-w-sm">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-amber-800">Warning</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Hai {rilievi.filter(rilievoSenzaData).length} Rilievi da Programmare
                  </p>
                </div>
                <button
                  onClick={() => setShowWarning(false)}
                  className="text-amber-500 hover:text-amber-700 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Admin: Riprogramma Rilievo */}
        <Dialog open={adminAction.type === "riprogramma"} onOpenChange={(o) => { if (!o) closeAdminAction(); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-blue-700">
                <Clock className="w-5 h-5" /> Riprogramma Rilievo
              </DialogTitle>
            </DialogHeader>
            {adminAction.rilievo && (
              <div className="space-y-3">
                <p className="text-sm text-slate-700">
                  Cliente: <strong>{adminAction.rilievo.cognome} {adminAction.rilievo.nome}</strong>
                </p>
                <p className="text-xs text-slate-500">
                  Attuale: {adminAction.rilievo.data_rilievo} alle {adminAction.rilievo.ora_rilievo}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs font-semibold">Nuova data</Label>
                    <Input type="date" value={adminAction.data} onChange={(e) => setAdminAction((p) => ({ ...p, data: e.target.value }))} data-testid="riprog-date" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Nuovo orario</Label>
                    <Input type="time" value={adminAction.orario} onChange={(e) => setAdminAction((p) => ({ ...p, orario: e.target.value }))} data-testid="riprog-time" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Nota per il cliente <span className="text-slate-400 font-normal">(opz.)</span></Label>
                  <Textarea rows={2} value={adminAction.nota} onChange={(e) => setAdminAction((p) => ({ ...p, nota: e.target.value }))} placeholder="Es: cambio orario per impegno improvviso..." data-testid="riprog-note" />
                </div>
                <p className="text-xs text-slate-500 italic">
                  Il cliente riceverà email di avviso + copia a showroomingenio@gmail.com. L'evento Google Calendar verrà spostato.
                </p>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={closeAdminAction} disabled={adminAction.loading}>Annulla</Button>
                  <Button type="button" onClick={confirmRiprogramma} disabled={adminAction.loading} data-state="colored" style={{ backgroundColor: "#2563eb" }} className="text-white hover:opacity-90" data-testid="riprog-confirm">
                    {adminAction.loading ? "Riprogrammazione..." : "Riprogramma e Avvisa"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Admin: Cancella Rilievo */}
        <Dialog open={adminAction.type === "cancella"} onOpenChange={(o) => { if (!o) closeAdminAction(); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <XCircle className="w-5 h-5" /> Cancella Rilievo
              </DialogTitle>
            </DialogHeader>
            {adminAction.rilievo && (
              <div className="space-y-3">
                <p className="text-sm text-slate-700">
                  Cliente: <strong>{adminAction.rilievo.cognome} {adminAction.rilievo.nome}</strong>
                </p>
                <p className="text-xs text-slate-500">
                  Fissato per: {adminAction.rilievo.data_rilievo} alle {adminAction.rilievo.ora_rilievo}
                </p>
                <div>
                  <Label className="text-xs font-semibold">Motivo (verrà inviato al cliente)</Label>
                  <Textarea rows={3} value={adminAction.motivo} onChange={(e) => setAdminAction((p) => ({ ...p, motivo: e.target.value }))} placeholder="Es: imprevisto del tecnico..." data-testid="cancella-motivo" />
                </div>
                <p className="text-xs text-slate-500 italic">
                  Il cliente riceverà email di cancellazione + copia a showroomingenio@gmail.com. L'evento Google Calendar verrà rimosso.
                </p>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={closeAdminAction} disabled={adminAction.loading}>Annulla</Button>
                  <Button type="button" onClick={confirmCancella} disabled={adminAction.loading} data-state="colored" style={{ backgroundColor: "#dc2626" }} className="text-white hover:opacity-90" data-testid="cancella-confirm">
                    {adminAction.loading ? "Cancellazione..." : "Cancella e Avvisa"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </PageLayout>
  );
}
